import Pill from '@pelckmans/elementis/components/pill';
import Tooltip from '@pelckmans/elementis/components/tooltip';
import { useTranslation } from 'react-i18next';
import IdentityContext from '../contexts/IdentityContext';
import { ComponentProps, useContext } from 'react';
import { Module } from '../api/module';
import { hasAccessToModule } from '../utils/identity';
import useLicenses from '../hooks/useLicenses';

const BASE = 'PAGES.CATALOGUE.MODULE.STATUS';

function ModuleStatus({
  module,
  demoModules,
  showUnlicensedStatus = false,
  showTooltip = true,
}: {
  module: Module;
  demoModules?: string[];
  showUnlicensedStatus?: boolean;
  showTooltip?: boolean;
}) {
  const { identity } = useContext(IdentityContext);
  const { t } = useTranslation();
  const { id, publishingState, incomplete } = module;
  const { isModuleLicensed } = useLicenses();

  const access = hasAccessToModule(identity, publishingState, module.id);
  const isAuthor = identity.author_modules.includes(module.id);
  const isDemo = demoModules?.includes(id);
  const isUnlicensed =
    showUnlicensedStatus &&
    (module.publishingState === 'published' || module.publishingState === 'phasedOut') &&
    !isModuleLicensed(module);
  const role = t(`ROLES.${isAuthor ? 'AUTHOR' : identity.highestRole.toUpperCase()}`);

  const renderStatus = ({
    key,
    label,
    tooltip,
    prominence,
  }: {
    key: string;
    label: string;
    tooltip?: string;
    prominence: ComponentProps<typeof Pill>['prominence'];
  }) => {
    const pill = (
      <Pill tooltip={showTooltip} prominence={prominence} role="status" key={key}>
        {label}
      </Pill>
    );

    if (!showTooltip || !tooltip) {
      return pill;
    }

    return (
      <Tooltip key={key} truncation="wrapped" anchor={pill}>
        {tooltip}
      </Tooltip>
    );
  };

  return (
    <>
      {publishingState === 'preview' &&
        renderStatus({
          key: `preview-${id}`,
          label: t(`${BASE}.PREVIEW.LABEL`),
          tooltip: t(`${BASE}.PREVIEW.TOOLTIP.${access ? 'ACCESS' : 'NO_ACCESS'}`, { role }),
          prominence: 'info',
        })}
      {publishingState === 'review' &&
        renderStatus({
          key: `review-${id}`,
          label: t(`${BASE}.REVIEW.LABEL`),
          tooltip: t(`${BASE}.REVIEW.TOOLTIP.VALUE`, { role }),
          prominence: 'info',
        })}
      {publishingState === 'phasedOut' &&
        renderStatus({
          key: `phasedOut-${id}`,
          label: t(`${BASE}.PHASED_OUT.LABEL`),
          tooltip: t(`${BASE}.PHASED_OUT.TOOLTIP.VALUE`, { role }),
          prominence: 'warning',
        })}
      {isUnlicensed &&
        renderStatus({
          key: `unlicensed-${id}`,
          label: t(`${BASE}.UNLICENSED.LABEL`),
          tooltip: t(`${BASE}.UNLICENSED.TOOLTIP.VALUE`),
          prominence: 'danger',
        })}
      {incomplete &&
        renderStatus({
          key: `incomplete-${id}`,
          label: t(`${BASE}.INCOMPLETE.LABEL`),
          tooltip: t(`${BASE}.INCOMPLETE.TOOLTIP.VALUE`),
          prominence: 'warning',
        })}
      {isDemo &&
        renderStatus({
          key: `demo-${id}`,
          label: t(`${BASE}.DEMO.LABEL`),
          tooltip: t(`${BASE}.DEMO.TOOLTIP.VALUE`),
          prominence: 'danger',
        })}
    </>
  );
}

export default ModuleStatus;
